import { insuranceI18n } from '@insurance/i18n'
import { paths } from '@insurance/routes'
import { usePermissionRestriction, useSessionContext } from '@shared/hooks'
import { AppPermissions } from '@shared/types'
import {
  Activity,
  AlignBottom,
  Bag,
  Book1,
  DocumentCopy,
  DocumentText1,
  ExternalDrive,
  Home2,
  Moneys,
  People,
  Profile,
  Setting2,
  TaskSquare,
  UserOctagon,
  Warning2
} from 'iconsax-react'
import { useTranslation } from 'react-i18next'

export const useMenuItems = () => {
  const { t } = useTranslation('insurance', {
    i18n: insuranceI18n,
    bindI18n: 'languageChanged languageChanging'
  })

  const { hasPermission } = usePermissionRestriction()
  const { isAdmin, broker } = useSessionContext()

  const menuItems = [
    ...(hasPermission([AppPermissions.SECTION_CUSTOMERS])
      ? [
          {
            key: 'clients',
            name: t('clients'),
            path: paths.clients.root,
            icon: <Home2 size={20} />
          }
        ]
      : []),
    ...(hasPermission([
      AppPermissions.SECTION_ORDERS,
      AppPermissions.INSPECTION,
      AppPermissions.ONLINE_ORDERS
    ])
      ? [
          {
            key: 'orders',
            name: t('orders'),
            path: paths.orders.root,
            icon: <Profile size={20} />
          }
        ]
      : []),
    ...(isAdmin
      ? [
          {
            key: 'products',
            name: t('products'),
            path: paths.products.root,
            icon: <UserOctagon size={20} />
          }
        ]
      : []),

    ...(hasPermission([AppPermissions.SECTION_SALES])
      ? [
          {
            key: 'invoices',
            name: t('invoices'),
            path: paths.invoices.root,
            icon: <DocumentText1 size={20} />,
            hideChildrenInMenu: true,
            children: [
              {
                key: 'transactions',
                name: t('transactions'),
                path: paths.transactions.root
              }
            ]
          }
        ]
      : []),

    ...(hasPermission([AppPermissions.SECTION_FINANCIAL])
      ? [
          {
            key: 'financial',
            name: t('financial'),
            path: paths.financial.root,
            icon: <Moneys size={20} />,
            children: [
              {
                key: paths.financial.bankTransfer,
                name: t('financial_bank_transfer_menu'),
                path: paths.financial.bankTransfer
              },
              {
                key: paths.financial.installments,
                name: t('financial_installments_menu'),
                path: paths.financial.installments
              },
              {
                key: paths.financial.overdueInstallments,
                name: t('financial_overdue_installments_menu'),
                path: paths.financial.overdueInstallments
              }
            ]
          }
        ]
      : []),
    ...(hasPermission([AppPermissions.SECTION_REPORTS])
      ? [
          {
            key: 'statistics',
            name: t('statistics'),
            path: paths.statistics.root,
            icon: <TaskSquare size={20} />
          }
        ]
      : []),
    ...(hasPermission([AppPermissions.SECTION_EXTERNAL_API])
      ? [
          {
            key: 'external-apis',
            name: t('external_apis'),
            path: paths.externalApis.root,
            icon: <ExternalDrive size={20} />
          }
        ]
      : []),
    ...(hasPermission([
      AppPermissions.SECTION_SETTINGS,
      AppPermissions.MANAGE_RESERVE_TYPES,
      AppPermissions.CLAIM_CASE_RCAI_EXPERT,
      AppPermissions.CLAIM_CASE_RCAI_SUPERVISOR
    ])
      ? [
          {
            key: 'nomenclatures',
            name: t('nomenclatures'),
            path: paths.nomenclatures.root,
            icon: <Book1 size={20} />,
            children: [
              ...(hasPermission([AppPermissions.SECTION_SETTINGS])
                ? [
                    {
                      key: 'settings-vehicle',
                      name: t('settings_vehicle'),
                      path: paths.nomenclatures.vehicleSettingsBrands
                    }
                  ]
                : []),
              ...(hasPermission([AppPermissions.MANAGE_RESERVE_TYPES])
                ? [
                    {
                      key: 'reserve_nomenclature',
                      name: t('reserve_nomenclature'),
                      path: paths.nomenclatures.reserveTypes
                    }
                  ]
                : []),
              ...(hasPermission([
                AppPermissions.CLAIM_CASE_RCAI_EXPERT,
                AppPermissions.CLAIM_CASE_RCAI_SUPERVISOR
              ])
                ? [
                    {
                      key: 'repair_stations',
                      name: t('repair_stations'),
                      path: paths.nomenclatures.repairStations
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),
    ...(hasPermission([AppPermissions.SECTION_SETTINGS])
      ? [
          {
            key: 'settings',
            name: t('settings'),
            path: paths.settings.root,
            icon: <Setting2 size={20} />,
            children: [
              {
                key: 'settings-general',
                name: t('settings_general'),
                path: paths.settings.general
              },
              {
                key: 'settings-ecec',
                name: t('settings_ecec'),
                path: paths.settings.ecec
              },
              {
                key: 'request-1c-logs',
                name: t('request_log_1C'),
                path: paths.settings.request1CLogs.root
              },
              {
                key: 'request-ecec-logs',
                name: t('request_log_ecec'),
                path: paths.settings.requestEcecLogs.root
              },
              ...(isAdmin
                ? [
                    {
                      key: 'cash-boxes',
                      name: t('cash_boxes'),
                      path: paths.paymentsConfig.cashBoxes
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),

    ...(hasPermission([
      AppPermissions.SALES_PANEL,
      AppPermissions.ONLINE_ORDERS
    ])
      ? [
          {
            key: 'sales-panel',
            name: t('sales_panel'),
            path: paths.salesPanel.root,
            icon: <Bag size={20} />
          }
        ]
      : []),

    ...(broker?.user?.is_broker_admin
      ? [
          {
            key: 'staff',
            name: t('staff'),
            path: paths.staff.agents,
            icon: <People size={18} />,
            wizard: true,
            hideChildrenInMenu: true,
            children: [
              {
                key: 'agents',
                name: t('agents'),
                path: paths.staff.agents
              },
              {
                key: 'teams',
                name: t('teams'),
                path: paths.staff.teams
              }
            ]
          }
        ]
      : []),

    ...(hasPermission([
      AppPermissions.RCA_DOCUMENTS,
      AppPermissions.RCA_MARK_AS_DELETED
    ])
      ? [
          {
            key: 'rca-data',
            name: t('rca_data'),
            path: paths.rcaDocuments.root,
            icon: <DocumentCopy size={20} />,
            children: [
              ...(hasPermission([AppPermissions.RCA_DOCUMENTS])
                ? [
                    {
                      key: 'rca-documents',
                      name: t('rcai_rcae_contracts'),
                      path: paths.rcaDocuments.root,
                      hideChildrenInMenu: true,
                      children: [
                        {
                          key: 'rca-green-card-documents',
                          name: t('rca_green_card_documents'),
                          path: paths.rcaGreenCardDocuments.root
                        }
                      ]
                    }
                  ]
                : []),
              ...(hasPermission([AppPermissions.RCA_MARK_AS_DELETED])
                ? [
                    {
                      key: 'rca-canceled',
                      name: t('contracts_cancel'),
                      path: paths.rcaCanceledDocuments.root,
                      hideChildrenInMenu: true,
                      children: [
                        {
                          key: 'rca-green-card-documents',
                          name: t('rca_green_card_documents'),
                          path: paths.rcaGreenCardCanceledDocuments.root
                        }
                      ]
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),

    ...(hasPermission([AppPermissions.SECTION_REGISTERS])
      ? [
          {
            key: 'reports',
            name: t('reports'),
            path: paths.reports.root,
            icon: <Activity size={20} />,
            children: [
              {
                key: 'health-insurance-contracts-register',
                name: t('health_insurance_contracts_register_menu'),
                path: paths.reports.healthInsuranceContractsRegister
              },
              {
                key: 'accident-insurance-contracts-register',
                name: t('accident_insurance_contracts_register_menu'),
                path: paths.reports.accidentInsuranceContractsRegister
              },
              {
                key: 'casco-insurance-contracts-register',
                name: t('casco_insurance_contracts_register_menu'),
                path: paths.reports.cascoInsuranceContractsRegister
              },
              {
                key: 'property-insurance-contracts-register',
                name: t('property_insurance_contracts_register_menu'),
                path: paths.reports.propertyInsuranceContractsRegister
              }
            ]
          }
        ]
      : []),

    ...(hasPermission([AppPermissions.CONTRACTS_REGISTRY])
      ? [
          {
            key: 'bank-summary',
            name: t('bank_summary'),
            path: paths.bankSummary.root,
            icon: <AlignBottom size={20} />,
            children: [
              ...(hasPermission([AppPermissions.PRODUCT_RCA])
                ? [
                    {
                      key: 'rca',
                      name: t('rca'),
                      path: paths.bankSummary.rca
                    }
                  ]
                : []),
              ...(hasPermission([AppPermissions.PRODUCT_GREEN_CARD])
                ? [
                    {
                      key: 'green-card',
                      name: t('green_card'),
                      path: paths.bankSummary.greenCard
                    }
                  ]
                : []),
              ...(hasPermission([AppPermissions.PRODUCT_MEDICAL_TRAVEL])
                ? [
                    {
                      key: 'medical',
                      name: t('medical'),
                      path: paths.bankSummary.medical
                    }
                  ]
                : []),
              ...(hasPermission([AppPermissions.PRODUCT_ACCIDENTS])
                ? [
                    {
                      key: 'accidents',
                      name: t('accidents'),
                      path: paths.bankSummary.accidents
                    }
                  ]
                : []),
              ...(hasPermission([AppPermissions.PRODUCT_CASCO])
                ? [
                    {
                      key: 'casco',
                      name: t('casco'),
                      path: paths.bankSummary.casco
                    }
                  ]
                : []),
              ...(hasPermission([AppPermissions.PRODUCT_PROPERTY])
                ? [
                    {
                      key: 'property',
                      name: t('property'),
                      path: paths.bankSummary.property
                    }
                  ]
                : []),
              ...(hasPermission([AppPermissions.PRODUCT_FINANCIAL])
                ? [
                    {
                      key: 'financial',
                      name: t('financial_risk'),
                      path: paths.bankSummary.financial
                    }
                  ]
                : []),
              ...(hasPermission([AppPermissions.PRODUCT_GUARANTEES])
                ? [
                    {
                      key: 'guarantees',
                      name: t('guarantees'),
                      path: paths.bankSummary.guarantees
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),

    ...(hasPermission([AppPermissions.SECTION_CLAIMS])
      ? [
          {
            key: 'claims',
            name: t('compensations'),
            path: paths.claims.root,
            icon: <Warning2 size={20} />,
            children: [
              {
                key: 'rcai_notifications',
                name: t('rcai_notifications'),
                path: paths.claims.notifications
              },
              ...(hasPermission([AppPermissions.SECTION_CLAIM_CASE_RCAI])
                ? [
                    {
                      key: 'rcai_claims_files',
                      name: t('rcai_claims_files'),
                      path: paths.claims.files
                    }
                  ]
                : []),
              ...(hasPermission([AppPermissions.SECTION_CLAIM_CASE_RCAE])
                ? [
                    {
                      key: 'rcae_cases',
                      name: t('rcae_cases'),
                      path: paths.claims.casesRcae
                    }
                  ]
                : [])
            ]
          }
        ]
      : [])
  ]

  return menuItems
}
